
import { Component, Prop, Vue, Watch, Ref } from "vue-property-decorator";

interface SubtitleCue {
  startTime: number;
  endTime: number;
  text: string;
}

@Component
export default class AudioPlayerWithSubtitles extends Vue {
  // Props mit Decorators
  @Prop({ required: true }) private audioSrc!: string;
  @Prop({ default: "audio/mpeg" }) private audioType!: string;
  @Prop({ default: "" }) private subtitleSrc!: string;
  @Prop({ default: "de" }) private subtitleLang!: string;
  @Prop({ default: "Deutsch" }) private subtitleLabel!: string;
  @Prop({ default: false }) private allowSubtitleUpload!: boolean;
  @Prop({ default: false }) private preventSeeking!: boolean;

  // Refs mit Decorator
  @Ref("audioPlayer") readonly audioPlayer!: HTMLAudioElement;
  @Ref("subtitleTrack") readonly subtitleTrack!: HTMLTrackElement;

  // Data properties
  private subtitleText: string = "";
  private subtitleFile: File | null = null;
  private cues: SubtitleCue[] = [];
  private activeCueIndex: number | null = null;
  private subtitleObjectUrl: string | null = null;

  private emitAudioEnded() {
    this.$emit("audio-ended");
  }

  // Lifecycle hooks
  mounted(): void {
    // Parse Cues, sobald die Komponente gemountet ist
    this.$nextTick(() => {
      setTimeout(() => {
        this.parseCues();
        // Spulen unterdrücken, falls aktiviert
        if (this.preventSeeking && this.audioPlayer) {
          let lastTime = 0;
          this.audioPlayer.addEventListener("timeupdate", () => {
            if (this.audioPlayer.currentTime > lastTime + 0.5) {
              this.audioPlayer.currentTime = lastTime;
            } else {
              lastTime = this.audioPlayer.currentTime;
            }
          });
        }
        // Ende erkennen
        this.audioPlayer.addEventListener("ended", this.emitAudioEnded);
      }, 500);
    });
  }

  beforeDestroy(): void {
    // cleanup URL-Objekte if any
    if (this.subtitleObjectUrl) {
      URL.revokeObjectURL(this.subtitleObjectUrl);
    }
    if (this.audioPlayer) {
      this.audioPlayer.removeEventListener("ended", this.emitAudioEnded);
    }
  }

  // Methods
  private updateSubtitle(): void {
    if (this.cues.length === 0) {
      return;
    }

    const currentTime = this.audioPlayer.currentTime;
    let text = "";

    // Finde den aktiven Cue basierend auf der aktuellen Zeit
    for (let i = 0; i < this.cues.length; i++) {
      const cue = this.cues[i];
      if (currentTime >= cue.startTime && currentTime <= cue.endTime) {
        text = cue.text;
        this.activeCueIndex = i;
        break;
      }
    }

    this.subtitleText = text;
  }

  private parseCues(): void {
    // Parse VTT file und lade die Cues
    if (this.subtitleTrack && this.subtitleTrack.track) {
      const track = this.subtitleTrack.track;

      // Warten bis die Cues geladen sind
      const loadCues = () => {
        if (track.cues) {
          this.cues = Array.from(track.cues).map((cue) => ({
            startTime: cue.startTime,
            endTime: cue.endTime,
            text:  (cue as VTTCue).text,
          }));
        }
      };

      if (track.cues) {
        loadCues();
      } else {
        track.addEventListener("load", loadCues);
      }
    }
  }

  private async handleSubtitleUpload(): Promise<void> {
    if (!this.subtitleFile) return;

    try {
      if (this.subtitleObjectUrl) {
        URL.revokeObjectURL(this.subtitleObjectUrl);
      }

      const subtitleUrl = URL.createObjectURL(this.subtitleFile);

      // save for later cleanup
      this.subtitleObjectUrl = subtitleUrl;

      // set new subtitle source
      this.$nextTick(() => {
        // remove old track if available
        const oldTracks = this.audioPlayer.getElementsByTagName("track");
        while (oldTracks[0]) {
          oldTracks[0].remove();
        }

        // Erstelle neuen Track
        const track = document.createElement("track");
        track.kind = "subtitles";
        track.src = subtitleUrl;
        track.srclang = this.subtitleLang;
        track.label = this.getFileName(this.subtitleFile!.name);
        track.default = true;

        // Füge den Track zum Audio-Element hinzu
        this.audioPlayer.appendChild(track);

        // Parse die neuen Cues (mit Timeout für Ladezeit)
        setTimeout(() => {
          // Da wir den Track manuell erstellt haben, müssen wir auf die Cues zugreifen
          const textTrack = this.audioPlayer.textTracks[0];
          if (textTrack) {
            const loadNewCues = () => {
              if (textTrack.cues) {
                this.cues = Array.from(textTrack.cues).map((cue) => ({
                  startTime: cue.startTime,
                  endTime: cue.endTime,
                  text:  (cue as VTTCue).text,
                }));
              }
            };

            if (textTrack.cues) {
              loadNewCues();
            } else {
              textTrack.addEventListener("load", loadNewCues);
            }
          }
        }, 100);
      });
    } catch (error) {
      console.error("Fehler beim Laden der Untertitel:", error);
    }
  }

  private getFileName(fullName: string): string {
    return fullName.split(".")[0];
  }
}
